import React from "react";

import { twMerge } from "tailwind-merge";

import { GenericTooltip, InfoCircle } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  CLICKS_AND_VIEWS_ENHANCED_MODEL,
  useUpsellEnhancedModel,
} from "@features/clicks-and-view-enhanced-modelling/use-upsell-enhanced-model";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { Chip } from "@shared/buttons/chip";

import { type ExtendableSelectProps } from "./select";

import { Select } from ".";

interface SelectOption {
  value: string;
  label: string;
}

interface AttributionModelSelectProps extends ExtendableSelectProps {
  label?: string | React.ReactElement;
  className?: string;
  isLoading?: boolean;
  attributionModel: string | null;
  onChange: (obj: any) => void;
  options?: SelectOption[];
}

export function AttributionModelSelect({
  label = "ATTRIBUTION MODEL",
  className,
  isLoading = false,
  attributionModel,
  onChange,
  options,
  ...props
}: AttributionModelSelectProps) {
  const { user } = useUser();

  const amEnum = user.attributionMethodEnum;
  const { partnersForEnhancedModelLogosWithOverlay, tryEnhancedModelCard } =
    useUpsellEnhancedModel();

  const labelTooltip = (
    <LearnMoreTooltip
      title="Attribution Model"
      content="Attribution models allow you to choose the method by which each ad interaction receives credit for your conversions."
      link="https://docs.northbeam.io/docs/attribution-models"
    />
  );

  const labelWithTooltip = label && (
    <>
      {label} {labelTooltip}
    </>
  );

  const selectOptions = options ?? amEnum.reactSelectOptions;

  const hasEnhancedModelInOptions = selectOptions.some(
    (option) => option.value === CLICKS_AND_VIEWS_ENHANCED_MODEL,
  );

  const getAttributionModelOptionClassName = ({
    isActive,
    isSelected,
    isDisabled,
  }: {
    isActive: boolean;
    isSelected: boolean;
    isDisabled?: boolean;
  }) =>
    twMerge(
      isActive && "bg-cosmic-5",
      isSelected && !isDisabled
        ? "bg-cosmic-3 font-semibold"
        : "hover:bg-cosmic-4 font-normal",
      isDisabled && "bg-gray-3 pointer-events-none",
      "px-3 py-2 cursor-pointer text-interstellar flex hover:font-semibold",
    );

  return (
    <Select
      label={labelWithTooltip}
      className={className}
      isLoading={isLoading}
      value={attributionModel}
      options={selectOptions}
      menuClassName={hasEnhancedModelInOptions ? "w-[20rem]" : "w-[18rem]"}
      onChange={onChange}
      menuItemRender={({ option, active, selected }) =>
        option.value === CLICKS_AND_VIEWS_ENHANCED_MODEL ? (
          <div>
            <GenericTooltip
              v2Styling
              overlayClassName="p-0 rounded-md shadow"
              content={tryEnhancedModelCard}
              placement="right"
              noInfoCircle
            >
              <div
                className={getAttributionModelOptionClassName({
                  isActive: active,
                  isSelected: selected,
                  isDisabled: option.disabled,
                })}
              >
                <span className="flex flex-grow items-center">
                  <Chip className="mr-2 bg-venus+0" text="NEW" />
                  <div className="mr-2 flex-grow">{option.label}</div>
                  {partnersForEnhancedModelLogosWithOverlay}{" "}
                </span>
                <InfoCircle v2Styling />
              </div>
            </GenericTooltip>
          </div>
        ) : (
          <div
            className={getAttributionModelOptionClassName({
              isActive: active,
              isSelected: selected,
              isDisabled: option.disabled,
            })}
          >
            <div>{option.label}</div>
          </div>
        )
      }
      {...props}
    />
  );
}
